import { borderLinesToCss, borderRadiusToCss, normalizeBorder } from "./borderUtils";
import { alignContentWrapHtml, buildImageInlineStyle } from "../utils/componentLayoutUtils";
import { defaultComponentBorder } from "./columnContentUtils";

const buttonBorderFromLegacy = (props) => {
  const border = normalizeBorder(props?.border || defaultComponentBorder());
  if (props?.borderRadius && !props?.border) {
    const radius = Number.parseInt(String(props.borderRadius).replace("px", ""), 10) || 0;
    if (radius > 0) {
      return { ...border, radius, top: { ...border.top, radius }, right: { ...border.right, radius }, bottom: { ...border.bottom, radius }, left: { ...border.left, radius } };
    }
  }
  return border;
};

export const COMPONENT_PRESETS = {
  "heading-h1": {
    key: "heading-h1",
    label: "Titolo H1",
    defaultProps: {
      text: "Titolo principale",
      align: "center",
      color: "#0b3d87",
      fontSize: "32px",
      fontWeight: "700",
      border: defaultComponentBorder(),
    },
  },
  "heading-h2": {
    key: "heading-h2",
    label: "Titolo H2",
    defaultProps: {
      text: "Sottotitolo",
      align: "left",
      color: "#0b3d87",
      fontSize: "24px",
      fontWeight: "700",
      border: defaultComponentBorder(),
    },
  },
  "heading-h3": {
    key: "heading-h3",
    label: "Titolo H3",
    defaultProps: {
      text: "Intestazione",
      align: "left",
      color: "#333333",
      fontSize: "18px",
      fontWeight: "600",
      border: defaultComponentBorder(),
    },
  },
  paragraph: {
    key: "paragraph",
    label: "Paragrafo",
    defaultProps: {
      text: "Testo del paragrafo",
      align: "left",
      color: "#333333",
      fontSize: "14px",
      fontWeight: "400",
      border: defaultComponentBorder(),
    },
  },
  image: {
    key: "image",
    label: "Immagine",
    defaultProps: {
      src: "",
      alt: "Immagine",
      widthPercent: 100,
      fixedWidthPx: 0,
      fixedHeightPx: 0,
      href: "",
      contentAlign: "center",
      contentVAlign: "center",
      border: defaultComponentBorder(),
    },
  },
  button: {
    key: "button",
    label: "Bottone",
    defaultProps: {
      text: "Scopri di più",
      href: "https://www.wowcheprezzi.it",
      backgroundColor: "#0b3d87",
      color: "#ffffff",
      align: "center",
      contentAlign: "center",
      contentVAlign: "center",
      buttonWidthPercent: 0,
      buttonPaddingX: 24,
      buttonPaddingY: 12,
      border: { ...defaultComponentBorder(), radius: 4 },
    },
  },
  "plain-html": {
    key: "plain-html",
    label: "Plain HTML",
    defaultProps: {
      html: "<p>HTML personalizzato</p>",
    },
  },
};

export const getComponentPreset = (key) => COMPONENT_PRESETS[key] || COMPONENT_PRESETS.paragraph;

export const listComponentPresets = () => Object.values(COMPONENT_PRESETS);

/** @param {{ publicUrl: string }} image @param {string} [alt] */
export const buildImageComponentPropsFromLibrary = (image, alt) => ({
  ...COMPONENT_PRESETS.image.defaultProps,
  src: image.publicUrl,
  alt: alt || "Immagine",
});

const escapeHtml = (value) =>
  String(value)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");

const escapeAttr = (value) => escapeHtml(value).replaceAll("'", "&#39;");

const escapeHtmlWithLineBreaks = (value) =>
  escapeHtml(value || "")
    .split("\n")
    .join("<br />");

const textStyle = (props) =>
  `text-align: ${props.align || "left"}; color: ${props.color || "#333333"}; font-size: ${props.fontSize || "14px"}; font-weight: ${props.fontWeight || "400"}; white-space: pre-line; margin: 0; box-sizing: border-box;`;

const textFrameStyle = (props) => {
  const parts = [textStyle(props)];
  if (props?.backgroundColor) {
    parts.push(`background-color: ${props.backgroundColor};`);
  }
  const lineCss = borderLinesToCss(props?.border);
  const radiusCss = borderRadiusToCss(props?.border);
  if (lineCss) {
    parts.push(lineCss);
  }
  if (radiusCss) {
    parts.push(radiusCss);
  }
  return parts.filter(Boolean).join(" ");
};

const COMPONENT_CLASS = {
  "heading-h1": "wcp-h1",
  "heading-h2": "wcp-h2",
  "heading-h3": "wcp-h3",
  paragraph: "wcp-p",
};

export const buildComponentHtml = (component) => {
  const { componentKey, props, overrides } = component;
  if (overrides?.html) {
    return overrides.html;
  }

  switch (componentKey) {
    case "heading-h1":
    case "heading-h2":
    case "heading-h3":
    case "paragraph": {
      const tag = componentKey === "paragraph" ? "p" : componentKey.replace("heading-", "");
      const cssClass = COMPONENT_CLASS[componentKey];
      return `<${tag} class="${cssClass}" style="${textFrameStyle(props)}">${escapeHtmlWithLineBreaks(props.text || "")}</${tag}>`;
    }
    case "image": {
      if (!props.src) {
        return `<p class="wcp-p wcp-img-placeholder">[Immagine non impostata]</p>`;
      }
      const frameLines = borderLinesToCss(props.border);
      const frameRadius = borderRadiusToCss(props.border);
      const imgStyle = [buildImageInlineStyle(props), frameLines, frameRadius].filter(Boolean).join("; ");
      const imgTag = `<img class="wcp-img" src="${escapeAttr(props.src)}" alt="${escapeAttr(props.alt || "")}" style="${imgStyle}" />`;
      const inner = props.href ? `<a href="${escapeAttr(props.href)}">${imgTag}</a>` : imgTag;
      return alignContentWrapHtml(props, inner);
    }
    case "button": {
      const border = buttonBorderFromLegacy(props);
      const lineCss = borderLinesToCss(border);
      const radiusCss = borderRadiusToCss(border);
      const widthPercent = props.buttonWidthPercent ?? 0;
      const widthRule = widthPercent > 0 ? `width:${widthPercent}%;` : "";
      const btnStyle = [
        "display:inline-block",
        "text-decoration:none",
        "box-sizing:border-box",
        "font-size:14px",
        "line-height:1.5",
        `background-color:${props.backgroundColor || "#0b3d87"}`,
        `color:${props.color || "#ffffff"}`,
        `text-align:${props.align || "center"}`,
        `padding:${props.buttonPaddingY ?? 12}px ${props.buttonPaddingX ?? 24}px`,
        lineCss,
        radiusCss,
        widthRule,
      ]
        .filter(Boolean)
        .join(";");
      const btnHtml = `<a class="wcp-btn" href="${escapeAttr(props.href || "#")}" style="${btnStyle}">${escapeHtml(props.text || "Bottone")}</a>`;
      return alignContentWrapHtml(
        {
          contentAlign: props.contentAlign || props.align,
          contentVAlign: props.contentVAlign,
        },
        btnHtml
      );
    }
    case "plain-html":
      return `<div class="wcp-html">${props.html || ""}</div>`;
    default:
      return `<p class="wcp-p">${escapeHtml(props.text || "")}</p>`;
  }
};
 